import React, {
  useMemo,
  useCallback,
  useState,
} from "react";
import { XMarkdown as Markdown } from "@ant-design/x-markdown";
import Latex from "@ant-design/x-markdown/plugins/Latex";
import { buildResourceUrl, downloadResource } from "@/shared/data";
import { MarkdownCode } from "./markdown-code";
import { removeEmptyMarkdownTables } from "./markdownPreprocess";
import {
  parseWorkspaceFileHref,
  type WorkspaceFileLink,
} from "./markdownWorkspaceLinks";

export type { WorkspaceFileLink } from "./markdownWorkspaceLinks";

interface MarkdownContentProps {
  content: string;
  onWorkspaceFileLinkClick?: (link: WorkspaceFileLink) => void;
}

type MarkdownPreProps = React.HTMLAttributes<HTMLPreElement> & {
  domNode?: unknown;
};


/**
 * Extracts the filename from a resource URL query string.
 * e.g. "/api/resource?file=chat_123%2Fjoke_01.md&download=true" → "joke_01.md"
 */
function extractFilenameFromResourceUrl(href: string): string {
  try {
    const url = new URL(href, window.location.origin);
    const file = url.searchParams.get("file") || "";
    const segments = file.split("/");
    return segments[segments.length - 1] || "download";
  } catch {
    return "download";
  }
}

/**
 * Returns true when the href points to the local resource API endpoint.
 */
function isResourceUrl(href: string): boolean {
  if (!href) return false;
  try {
    const url = new URL(href, window.location.origin);
    return url.pathname === "/api/resource";
  } catch {
    return href.startsWith("/api/resource");
  }
}

/**
 * Custom anchor component that intercepts `/api/resource` links and
 * downloads them via fetch with auth headers (Bearer token) instead
 * of letting the browser navigate directly (which causes 401).
 */
type AuthAnchorProps = React.AnchorHTMLAttributes<HTMLAnchorElement> & {
  onWorkspaceFileLinkClick?: (link: WorkspaceFileLink) => void;
};

const AuthAnchor: React.FC<AuthAnchorProps> = (props) => {
  const {
    href,
    children,
    onWorkspaceFileLinkClick,
    ...rest
  } = props;
  const [downloading, setDownloading] = useState(false);
  const downloadFilename = href && isResourceUrl(href)
    ? extractFilenameFromResourceUrl(href)
    : undefined;
  const workspaceFileLink = useMemo(
    () => parseWorkspaceFileHref(href),
    [href],
  );

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (workspaceFileLink && onWorkspaceFileLinkClick) {
        e.preventDefault();
        onWorkspaceFileLinkClick(workspaceFileLink);
        return;
      }

      if (!href || !isResourceUrl(href) || downloading) return;

      e.preventDefault();
      setDownloading(true);

      const filename = extractFilenameFromResourceUrl(href);
      void downloadResource(href, { filename })
        .catch((error: unknown) => {
          console.error("Resource download failed:", error);
        })
        .finally(() => {
          setDownloading(false);
        });
    },
    [href, downloading, onWorkspaceFileLinkClick, workspaceFileLink],
  );

  return (
    <a
      {...rest}
      href={href}
      download={downloadFilename || rest.download}
      onClick={handleClick}
    >
      {downloading ? "下载中…" : children}
    </a>
  );
};


const MarkdownPre: React.FC<MarkdownPreProps> = ({
  children,
  domNode: _domNode,
  ...rest
}) => {
  const childArray = React.Children.toArray(children);
  const onlyChild = childArray.length === 1 ? childArray[0] : null;
  if (
    React.isValidElement(onlyChild) 
		&&
    onlyChild.type === MarkdownCode
  ) {
    return <>{onlyChild}</>;
  }

  return <pre {...rest}>{children}</pre>;
};

/**
 * MarkdownContent wraps @ant-design/x-markdown Markdown component
 * for streaming-compatible Markdown rendering.
 *
 * Preserves:
 * - Code block rendering with syntax highlighting
 * - KaTeX math formula support (via CSS import)
 * - Image auth-src rewriting (data-auth-src → blob URL)
 * - Link safety filtering
 * - Authenticated resource downloads (via custom anchor component)
 */
export const MarkdownContent: React.FC<MarkdownContentProps> = ({
  content,
  onWorkspaceFileLinkClick,
}) => {
  const markdownConfig = useMemo(
    () => ({
      gfm: true,
      breaks: true,
      extensions: Latex(),
    }),
    [],
  );

  const markdownComponents = useMemo(
    () =>
      ({
        a: (anchorProps: React.AnchorHTMLAttributes<HTMLAnchorElement>) => (
          <AuthAnchor
            {...anchorProps}
            onWorkspaceFileLinkClick={onWorkspaceFileLinkClick}
          />
        ),
        code: MarkdownCode,
        pre: MarkdownPre,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      }) as any,
    [onWorkspaceFileLinkClick],
  );

  const processedContent = useMemo(() => {
    if (!content) return "";

    const withoutEmptyTables = removeEmptyMarkdownTables(content);

    /* Rewrite non-http image src to go through API proxy */
    return withoutEmptyTables.replace(
      /!\[([^\]]*)\]\((?!https?:\/\/)([^)\s]+)\)/g,
      (match, alt, src) => {
        if (src.startsWith("data:") || src.startsWith("blob:")) return match;
        const proxiedSrc = buildResourceUrl(src);
        return `![${alt}](${proxiedSrc})`;
      },
    );
  }, [content]);

  if (!processedContent) {
    return null;
  }

  return (
    <Markdown config={markdownConfig} components={markdownComponents} style={{fontSize: 14}}>
      {processedContent}
    </Markdown>
  );
};
